import React, { useEffect, useRef } from 'react';
import { EPerPages } from 'models/table';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { useNavigate } from 'react-router-dom';
import { Loader } from 'components/atoms/Loader';
import { Table } from 'components/molecules/Table';
import {
  AncillariesReferenceTableAmmunitionColumns,
  mapARTAmmunitionToDataSource,
} from 'pages/AncillariesPage/helpers';
import { ERouteLinks } from 'models/route';
import { EArtAncillaryTypeName } from 'constants/ArtAccessoryType';
import { Pagination } from 'components/molecules/Pagination';
import { CardList } from 'components/molecules/CardList';
import { AncillariesReferenceAmmunitionCard } from 'pages/AncillariesPage/AncillariesReferenceTablePage/AncillariesReferenceAmmunitionCard';

interface IAncillariesReferenceAmmunition {
  setAncillariesReferenceSort: (arg: any) => void;
  perPageAncillariesReference: EPerPages;
  setPerPageAncillariesReference: (arg: any) => void;
  currentPageAncillariesReference: number;
  setCurrentPageAncillariesReference: (arg: any) => void;
  isAncillariesReferenceLoading: boolean | null;
  paginatorAncillariesReference: any;
  ancillariesReference: any[];
  isTransactionPage?: boolean;
  LEId?: string;
  type?: string | null;
  isPaginationLoading: boolean;
  isQueryAncillariesReferenceLoading: boolean;
  isSortLoaded: boolean;
  isPaginationVisible: boolean;
}

const AncillariesReferenceAmmunition = ({
  setAncillariesReferenceSort,
  perPageAncillariesReference,
  setPerPageAncillariesReference,
  currentPageAncillariesReference,
  setCurrentPageAncillariesReference,
  isAncillariesReferenceLoading,
  paginatorAncillariesReference,
  ancillariesReference,
  isTransactionPage = false,
  LEId,
  type,
  isPaginationLoading,
  isQueryAncillariesReferenceLoading,
  isSortLoaded,
  isPaginationVisible,
}: IAncillariesReferenceAmmunition) => {
  const { isDesktop } = useThemeBreakpoint();
  const navigate = useNavigate();

  const scrollPosition = useRef(0);

  useEffect(() => {
    if (isSortLoaded) {
      window.scrollTo({ top: scrollPosition.current, behavior: 'auto' });
      scrollPosition.current = 0;
    }
  }, [isSortLoaded]);

  return (
    <div>
      {isAncillariesReferenceLoading || isQueryAncillariesReferenceLoading ? (
        <Loader isLoading={true} />
      ) : (
        <>
          {isDesktop ? (
            <Table
              columns={AncillariesReferenceTableAmmunitionColumns({
                isTransactionPage,
                handleViewTransaction: (id: string) =>
                  navigate(
                    `${ERouteLinks.LegalEntityViewAncillary.replace(':leId', LEId ?? '')
                      .replace(':id', id)
                      .replace(
                        ':type',
                        EArtAncillaryTypeName.AMMUNITION.toLowerCase(),
                      )}?type=${type}&expand=ancillaries`,
                  ),
                handleEdit: (id: string) =>
                  navigate(
                    ERouteLinks.EditAncillaries.replace(':id', id).replace(
                      ':type',
                      EArtAncillaryTypeName.AMMUNITION.toLowerCase(),
                    ),
                  ),
                handleSelect: (ancillary: any) =>
                  navigate(
                    `${ERouteLinks.LegalEntityAddAncillary.replace(
                      ':id',
                      LEId ?? '',
                    )}?type=${type}&expand=ancillaries`,
                    {
                      state: {
                        ancillary,
                        prevRoute: 'prevRoute',
                      },
                    },
                  ),
                LEId,
                type,
              })}
              dataSource={mapARTAmmunitionToDataSource(
                ancillariesReference,
                EArtAncillaryTypeName.AMMUNITION,
              )}
              onSort={(it) => {
                setAncillariesReferenceSort(it);
                scrollPosition.current = window.scrollY;
              }}
              isSortLoaded={isSortLoaded}
              customPaddingIfNoData="30px"
            />
          ) : (
            <CardList
              items={mapARTAmmunitionToDataSource(
                ancillariesReference,
                EArtAncillaryTypeName.AMMUNITION,
              )}
              render={(data, index) => (
                <AncillariesReferenceAmmunitionCard
                  data={data}
                  key={index}
                  handleView={(id: string) =>
                    navigate(
                      ERouteLinks.ViewAncillaries.replace(':id', id).replace(
                        ':type',
                        EArtAncillaryTypeName.AMMUNITION.toLowerCase(),
                      ),
                    )
                  }
                  handleEdit={(id: string) =>
                    navigate(
                      ERouteLinks.EditAncillaries.replace(':id', id).replace(
                        ':type',
                        EArtAncillaryTypeName.AMMUNITION.toLowerCase(),
                      ),
                    )
                  }
                  handleSelect={() =>
                    navigate(
                      `${ERouteLinks.LegalEntityAddAncillary.replace(
                        ':id',
                        LEId ?? '',
                      )}?type=${type}&expand=ancillaries`,
                      {
                        state: {
                          ancillary: data,
                          prevRoute: 'prevRoute',
                        },
                      },
                    )
                  }
                  isTransactionPage={isTransactionPage}
                />
              )}
            />
          )}

          {paginatorAncillariesReference && (
            <Pagination
              count={paginatorAncillariesReference?.totalElements}
              perPage={perPageAncillariesReference}
              onChangePage={(page) => {
                setCurrentPageAncillariesReference(page);
              }}
              onChangePerPage={(value) => {
                setPerPageAncillariesReference(value);
              }}
              current={currentPageAncillariesReference}
              isLoading={isPaginationLoading}
              isVisible={isPaginationVisible && isSortLoaded}
            />
          )}
        </>
      )}
    </div>
  );
};

export default AncillariesReferenceAmmunition;
